import {
    AfterViewInit,
    booleanAttribute,
    Directive,
    ElementRef,
    forwardRef,
    inject,
    input,
    isSignal,
    OnChanges,
    OnDestroy,
    output,
    Renderer2,
    SimpleChanges
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import flatpickr from "flatpickr";
import { DatepickerDefaults, DisableEnableDate } from "./defaults.service";

export interface DatepickerOutputOptions {
    selectedDates: Date[];
    dateString: string;
    instance: flatpickr.Instance;
}

export interface DatepickerDayCreateOutputOptions extends DatepickerOutputOptions {
    dayElement: HTMLElement;
}

export const DATEPICKER_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => UiKitDatepickerDirective), //tslint:disable-line
    multi: true
};

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: "input[uiKitDatepicker]",
    exportAs: "uiKitDatepicker",
    host: {
        "(blur)": "onTouchedFn()",
        "(input)": "inputChanged()"
    },
    standalone: true,
    providers: [DATEPICKER_CONTROL_VALUE_ACCESSOR]
})
export class UiKitDatepickerDirective implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
    private elm = inject(ElementRef);
    private defaults = inject(DatepickerDefaults);
    private renderer = inject(Renderer2);

    private instance: flatpickr.Instance | undefined;
    private isDisabled = false;
    private initialValue: unknown;

    /**
     * Exactly the same as date format, but for the altInput field.
     */
    public altFormat = input<string>();

    /**
     * 	Show the user a readable date (as per altFormat), but return something totally different to the server.
     */
    public altInput = input<boolean>();

    /**
     * This class will be added to the input element created by the altInput option.
     * Note that `altInput` already inherits classes from the original input.
     */
    public altInputClass = input<string>();

    /**
     * Allows the user to enter a date directly input the input field. By default, direct entry is disabled.
     */
    public allowInput = input<boolean>();

    /**
     * Instead of `body`, appends the calendar to the specified node instead.
     */
    public appendTo = input<HTMLElement>();

    /**
     * Indicates whether the datepicker is inside a CDK overlay
     */
    readonly insideCdkOverlay = input(false, { transform: booleanAttribute });

    /**
     * Defines how the date will be formatted in the aria-label for calendar days, using the same tokens as dateFormat. If you change this, you should choose a value that will make sense if a screen reader reads it out loud.
     */
    public ariaDateFormat = input<string>();

    /**
     * Whether clicking on the input should open the picker.
     * You could disable this if you wish to open the calendar manually `with.open()`.
     */
    public clickOpens = input<boolean>();

    /**
     * A string of characters which are used to define how the date will be displayed in the input box.
     * The supported characters are defined in the table below.
     */
    public dateFormat = input<string>();

    /**
     * Initial value of the hour element.
     */
    public defaultHour = input<number>();

    /**
     * Initial value of the minute element.
     */
    public defaultMinute = input<number>();

    /**
     * See <a href="https://chmln.github.io/flatpickr/examples/#disabling-specific-dates">disabling dates</a>.
     */
    public disable = input<DisableEnableDate[]>([]);

    /**
     * Set disableMobile to true to always use the non-native picker.
     * By default, Flatpickr utilizes native datetime widgets unless certain options (e.g. disable) are used.
     */
    public disableMobile = input<boolean>();

    /**
     * See <a href="https://chmln.github.io/flatpickr/examples/#disabling-all-dates-except-select-few">enabling dates</a>.
     */
    public enable = input<DisableEnableDate[] | undefined>(undefined);

    /**
     * Enables time picker.
     */
    public enableTime = input<boolean>();

    /**
     * Enables seconds in the time picker.
     */
    public enableSeconds = input<boolean>();

    /**
     * Allows using a custom date formatting function instead of the built-in handling for date formats using dateFormat, altFormat, etc.
     */
    public formatDate = input<(value: unknown) => string>();

    /**
     * Adjusts the step for the hour input (incl. scrolling).
     */
    public hourIncrement = input<number>();

    /**
     * Displays the calendar inline.
     */
    public inline = input<boolean>();

    /**
     * The maximum date that a user can pick to (inclusive).
     */
    public maxDate = input<string | Date>();

    /**
     * The minimum date that a user can start picking from (inclusive).
     */
    public minDate = input<string | Date>();

    /**
     * Adjusts the step for the minute input (incl. scrolling).
     */
    public minuteIncrement = input<number>();

    /**
     * Select a single date, multiple dates or a date range.
     */
    public mode = input<"single" | "multiple" | "range">();

    /**
     * HTML for the arrow icon, used to switch months.
     */
    public nextArrow = input<string>();

    /**
     * HTML for the left arrow icon.
     */
    public prevArrow = input<string>();

    /**
     * Hides the day selection in calendar. Use it along with `enableTime` to create a time picker.
     */
    public noCalendar = input<boolean>();

    /**
     * Provide a date for 'today', which will be used instead of "new Date()"
     */
    public now = input<Date | string | number>();

    /**
     * Function that expects a date string and must return a Date object.
     */
    public parseDate = input<(str: string) => Date>();

    /**
     * Show the month using the shorthand version (ie, Sep instead of September).
     */
    public shorthandCurrentMonth = input<boolean>();

    /**
     * Position the calendar inside the wrapper and next to the input element. (Leave `false` unless you know what you're doing).
     */
    public static = input<boolean>();

    /**
     * Displays time picker in 24 hour mode without AM/PM selection when enabled.
     */
    public time24hr = input<boolean>();

    /**
     * Enables display of week numbers in calendar.
     */
    public weekNumbers = input<boolean>();

    /**
     * You may override the function that extracts the week numbers from a Date by supplying a getWeek function.
     * It takes in a date as a parameter and should return a corresponding string that you want to appear left of every week.
     */
    public getWeek = input<(date: Date) => string>();

    /**
     * Custom elements and input groups.
     */
    public wrap = input<boolean>();

    /**
     * Array of plugin instances to use.
     */
    public plugins = input<unknown[]>();

    /**
     * The locale object or string to use for the locale.
     */
    public locale = input<Record<string, unknown> | string>();

    /**
     * Auto convert the ngModel value from a string to a date / array of dates / from - to date object depending on the `mode`
     */
    public convertModelValue = input<boolean>();

    /**
     * Gets triggered once the calendar is in a ready state
     */
    public flatpickrReady = output<DatepickerOutputOptions>();

    /**
     * Gets triggered when the user selects a date, or changes the time on a selected date.
     */
    public flatpickrChange = output<DatepickerOutputOptions>();

    /**
     * Gets triggered when the input value is updated with a new date string.
     */
    public flatpickrValueUpdate = output<DatepickerOutputOptions>();

    /**
     * Gets triggered when the calendar is opened.
     */
    public flatpickrOpen = output<DatepickerOutputOptions>();

    /**
     * Gets triggered when the calendar is closed.
     */
    public flatpickrClose = output<DatepickerOutputOptions>();

    /**
     * Gets triggered when the month is changed, either by the user or programmatically.
     */
    public flatpickrMonthChange = output<DatepickerOutputOptions>();

    /**
     * Gets triggered when the year is changed, either by the user or programmatically.
     */
    public flatpickrYearChange = output<DatepickerOutputOptions>();

    /**
     * Take full control of every date cell with this output
     */
    public flatpickrDayCreate = output<DatepickerDayCreateOutputOptions>();

    public onChangeFn: (value: unknown) => void = () => {};

    public onTouchedFn = () => {};

    public ngAfterViewInit(): void {
        const appendTo = this.insideCdkOverlay()
            ? (this.elm.nativeElement.closest(".cdk-overlay-pane") ?? this.appendTo())
            : this.appendTo();

        const options: Record<string, unknown> = {
            altFormat: this.altFormat(),
            altInput: this.altInput(),
            altInputClass: this.altInputClass(),
            allowInput: this.allowInput(),
            appendTo: appendTo,
            ariaDateFormat: this.ariaDateFormat(),
            clickOpens: this.clickOpens(),
            dateFormat: this.dateFormat(),
            defaultHour: this.defaultHour(),
            defaultMinute: this.defaultMinute(),
            disable: this.disable(),
            disableMobile: this.disableMobile(),
            enable: this.enable(),
            enableTime: this.enableTime(),
            enableSeconds: this.enableSeconds(),
            formatDate: this.formatDate(),
            hourIncrement: this.hourIncrement(),
            defaultDate: this.initialValue,
            inline: this.inline(),
            maxDate: this.maxDate(),
            minDate: this.minDate(),
            minuteIncrement: this.minuteIncrement(),
            mode: this.mode(),
            nextArrow: this.nextArrow(),
            noCalendar: this.noCalendar(),
            now: this.now(),
            parseDate: this.parseDate(),
            prevArrow: this.prevArrow(),
            shorthandCurrentMonth: this.shorthandCurrentMonth(),
            static: this.static(),
            time24hr: this.time24hr(),
            weekNumbers: this.weekNumbers(),
            getWeek: this.getWeek(),
            wrap: this.wrap(),
            plugins: this.plugins(),
            locale: this.locale(),
            onChange: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrChange.emit({ selectedDates, dateString, instance });
            },
            onOpen: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrOpen.emit({ selectedDates, dateString, instance });
            },
            onClose: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrClose.emit({ selectedDates, dateString, instance });
            },
            onMonthChange: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrMonthChange.emit({ selectedDates, dateString, instance });
            },
            onYearChange: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrYearChange.emit({ selectedDates, dateString, instance });
            },
            onReady: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrReady.emit({ selectedDates, dateString, instance });
            },
            onValueUpdate: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance) => {
                this.flatpickrValueUpdate.emit({ selectedDates, dateString, instance });
            },
            onDayCreate: (selectedDates: Date[], dateString: string, instance: flatpickr.Instance, dayElement: HTMLElement) => {
                this.flatpickrDayCreate.emit({
                    selectedDates,
                    dateString,
                    instance,
                    dayElement
                });
            }
        };

        Object.keys(options).forEach((key) => {
            if (typeof options[key] === "undefined") {
                options[key] = (this.defaults as unknown as Record<string, unknown>)[key];
            }
        });

        options.time_24hr = options.time24hr;

        if (!options.enable) {
            delete options.enable;
        }

        this.instance = flatpickr(this.elm.nativeElement, options) as flatpickr.Instance;
        this.setDisabledState(this.isDisabled);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const instance = this.instance;

        if (instance !== undefined) {
            Object.keys(changes).forEach((inputKey) => {
                if (isSignal((this as Record<string, unknown>)[inputKey])) {
                    instance.set(
                        inputKey as keyof flatpickr.Options.Options,
                        (this as unknown as Record<string, () => unknown>)[inputKey]()
                    );
                } else {
                    this.instance?.set(inputKey as keyof flatpickr.Options.Options, (this as Record<string, unknown>)[inputKey]);
                }
            });
        }
    }

    public ngOnDestroy(): void {
        this.instance?.destroy();
        this.instance = undefined;
    }

    public writeValue(value: unknown): void {
        let convertedValue = value;
        if (this.convertModelValue() && this.mode() === "range" && value && typeof value === "object") {
            const rangeValue = value as Record<string, unknown>;
            convertedValue = [rangeValue.from, rangeValue.to];
        }

        if (this.instance) {
            this.instance.setDate(convertedValue as flatpickr.Options.DateOption | flatpickr.Options.DateOption[]);
        } else {
            // flatpickr hasn't been initialised yet, store the value for later use
            this.initialValue = convertedValue;
        }
    }

    public registerOnChange(fn: (value: unknown) => void): void {
        this.onChangeFn = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (this.instance) {
            if (this.isDisabled) {
                this.renderer.setProperty(this.instance._input, "disabled", "disabled");
            } else {
                this.renderer.removeAttribute(this.instance._input, "disabled");
            }
        }
    }

    public inputChanged(): void {
        const value: string = this.elm.nativeElement.value;
        if (this.convertModelValue() && typeof value === "string" && this.instance) {
            switch (this.mode()) {
                case "multiple": {
                    const dates: (Date | undefined)[] = value
                        .split("; ")
                        .map((str) => this.instance?.parseDate(str, this.instance.config.dateFormat, !this.instance.config.enableTime));
                    this.onChangeFn(dates);
                    break;
                }

                case "range": {
                    const [from, to] = value
                        .split(this.instance.l10n.rangeSeparator)
                        .map((str) => this.instance?.parseDate(str, this.instance.config.dateFormat, !this.instance.config.enableTime));
                    this.onChangeFn({ from, to });
                    break;
                }

                case "single":
                default:
                    this.onChangeFn(this.instance.parseDate(value, this.instance.config.dateFormat, !this.instance.config.enableTime));
            }
        } else {
            this.onChangeFn(value);
        }
    }
}
